import React, { useEffect, useRef, useState } from 'react';
import { WebContainer } from '@webcontainer/api';
import { demoFiles } from './sandboxFiles';

/**
 * WebContainer POC — boots a Node runtime in the user's browser, mounts an
 * inlined jira-mock-style React + webpack project (the same architecture the
 * app-factory pipeline produces: @forge/bridge aliased to a local jira-mock
 * package, ts-loader compiling .ts/.tsx, html-webpack-plugin emitting the
 * shell HTML), runs `npm install` then `npm run start`, and iframes the
 * webpack-dev-server URL once `server-ready` fires.
 *
 * The point: does the full jira-mock-architecture toolchain actually run end
 * to end in WebContainer? Atlaskit is deliberately excluded for now so npm
 * install completes in ~60-90s; once this passes we can layer Atlaskit on.
 */

type Status =
  | 'idle'
  | 'booting'
  | 'mounting'
  | 'installing'
  | 'building'
  | 'ready'
  | 'error';

// WebContainer.boot() can be called at most once per page load. Stash the
// instance at module scope so React Strict Mode's double-effect run doesn't
// trip "Only a single WebContainer instance can be booted" errors.
let bootedInstance: WebContainer | null = null;
let bootInFlight: Promise<WebContainer> | null = null;

async function bootOnce(): Promise<WebContainer> {
  if (bootedInstance) return bootedInstance;
  if (bootInFlight) return bootInFlight;
  bootInFlight = WebContainer.boot({ coep: 'credentialless' }).then((wc) => {
    bootedInstance = wc;
    return wc;
  });
  return bootInFlight;
}

export function SandboxTest() {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [logLines, setLogLines] = useState<string[]>([]);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const startedRef = useRef(false);
  const logRef = useRef<HTMLPreElement | null>(null);

  function appendLog(chunk: string) {
    setLogLines((prev) => {
      const next = [...prev, chunk];
      return next.length > 400 ? next.slice(next.length - 400) : next;
    });
  }

  useEffect(() => {
    if (!logRef.current) return;
    logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [logLines]);

  async function run() {
    if (startedRef.current) return;
    startedRef.current = true;

    setError(null);
    setLogLines([]);
    setIframeUrl(null);

    try {
      if (!window.crossOriginIsolated) {
        throw new Error(
          'window.crossOriginIsolated is false — COOP/COEP headers are not in effect. ' +
            'Restart `npm start` so src/setupProxy.js is picked up, and reload this page.',
        );
      }

      setStatus('booting');
      appendLog('[poc] booting WebContainer …');
      const wc = await bootOnce();
      appendLog('[poc] booted.');

      setStatus('mounting');
      appendLog('[poc] mounting jira-mock demo project …');
      await wc.mount(demoFiles);
      appendLog('[poc] mounted.');

      setStatus('installing');
      appendLog('[poc] running `npm install` …');
      const install = await wc.spawn('npm', ['install']);
      install.output.pipeTo(
        new WritableStream({ write: (data) => appendLog(data) }),
      );
      const installExit = await install.exit;
      if (installExit !== 0) {
        throw new Error(`npm install exited with code ${installExit}`);
      }
      appendLog('[poc] install complete.');

      wc.on('server-ready', (port, url) => {
        appendLog(`[poc] server-ready on port ${port}: ${url}`);
        setIframeUrl(url);
        setStatus('ready');
      });

      setStatus('building');
      appendLog('[poc] running `npm run start` (webpack-dev-server) …');
      const server = await wc.spawn('npm', ['run', 'start']);
      server.output.pipeTo(
        new WritableStream({ write: (data) => appendLog(data) }),
      );
    } catch (e: any) {
      const msg = e?.message ?? String(e);
      appendLog(`[poc] ERROR: ${msg}`);
      setError(msg);
      setStatus('error');
      startedRef.current = false;
    }
  }

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui', maxWidth: 1200, margin: '0 auto' }}>
      <h1 style={{ marginTop: 0 }}>WebContainer POC</h1>
      <p style={{ color: '#555', marginTop: 4 }}>
        Boot a Node runtime in the browser, install Express, run a hello-world server,
        iframe the result. If this works, the rest of the in-browser execution story
        is on the table.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <button
          onClick={run}
          disabled={status !== 'idle' && status !== 'error'}
          style={{
            padding: '8px 16px',
            background: status === 'ready' ? '#4caf50' : '#1976d2',
            color: 'white',
            border: 0,
            borderRadius: 6,
            cursor: status !== 'idle' && status !== 'error' ? 'not-allowed' : 'pointer',
            fontSize: 14,
          }}
        >
          {status === 'idle' || status === 'error' ? 'Run' : status}
        </button>
        <StatusPill status={status} />
        <span style={{ color: '#888', fontSize: 12 }}>
          crossOriginIsolated: <code>{String(window.crossOriginIsolated)}</code>
        </span>
      </div>

      {error && (
        <div
          style={{
            padding: 12,
            background: '#fdecea',
            border: '1px solid #f5c6cb',
            borderRadius: 6,
            color: '#611a15',
            marginBottom: 16,
            whiteSpace: 'pre-wrap',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, height: 540 }}>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>logs</div>
          <pre
            ref={logRef}
            style={{
              flex: 1,
              background: '#111',
              color: '#9fef9f',
              padding: 12,
              borderRadius: 6,
              margin: 0,
              overflow: 'auto',
              fontSize: 12,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              whiteSpace: 'pre-wrap',
            }}
          >
            {logLines.join('')}
          </pre>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
            preview {iframeUrl && <span style={{ color: '#999' }}>({iframeUrl})</span>}
          </div>
          {iframeUrl ? (
            <iframe
              title="webcontainer preview"
              src={iframeUrl}
              style={{ flex: 1, border: '1px solid #ddd', borderRadius: 6, background: 'white' }}
            />
          ) : (
            <div
              style={{
                flex: 1,
                border: '1px dashed #ccc',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#aaa',
                background: '#fafafa',
              }}
            >
              {status === 'idle' ? 'Click Run to start.' : 'waiting for server-ready …'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: Status }) {
  const map: Record<Status, { label: string; bg: string; fg: string }> = {
    idle: { label: 'idle', bg: '#eee', fg: '#555' },
    booting: { label: 'booting', bg: '#fff4d6', fg: '#7a5a00' },
    mounting: { label: 'mounting', bg: '#fff4d6', fg: '#7a5a00' },
    installing: { label: 'npm install', bg: '#fff4d6', fg: '#7a5a00' },
    building: { label: 'webpack build', bg: '#fff4d6', fg: '#7a5a00' },
    ready: { label: 'ready', bg: '#e3f6e3', fg: '#1b5e20' },
    error: { label: 'error', bg: '#fdecea', fg: '#611a15' },
  };
  const s = map[status];
  return (
    <span
      style={{
        padding: '2px 10px',
        background: s.bg,
        color: s.fg,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
      }}
    >
      {s.label}
    </span>
  );
}
